<template>
  <div class="budget-cell group relative" :class="cellClass">
    <!-- View mode (always rendered to keep column width stable, hidden when editing) -->
    <button
      class="w-full h-full flex items-center justify-end gap-1 px-2 py-1.5 text-right text-sm font-medium tabindex-cell focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary rounded transition-colors disabled:opacity-100"
      :class="[valueClass, { 'invisible': isEditing }]"
      @click="startEdit"
      :aria-disabled="isNonEditable"
      :tabindex="isNonEditable ? -1 : 0"
      :title="editTitle"
      :aria-label="`${categoryName}, ${monthLabel} : ${displayValue}`"
    >
        <!-- Formula indicator -->
        <span
          v-if="hasFormula && !isManualOverride && !isNonEditable"
          class="text-[9px] font-black opacity-50 select-none"
          title="Valeur calculée par formule"
        >ƒ</span>

        <!-- Manual override indicator on formula cell -->
        <span
          v-if="hasFormula && isManualOverride && !isNonEditable"
          class="text-[9px] opacity-40 select-none"
          title="Valeur saisie manuellement (formule ignorée)"
        >🔒</span>

        <!-- Locked / Non-editable indicator -->
        <span
          v-if="isNonEditable"
          class="text-[9px] opacity-60 select-none flex items-center justify-center shrink-0"
          title="Cette catégorie est non modifiable manuellement (définie par formule)"
        >
          <svg class="w-2.5 h-2.5 text-zinc-400 dark:text-zinc-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </span>

        <span class="tabular-nums">{{ displayValue }}</span>
      </button>

    <button
      v-if="hasFormula && isManualOverride && storedValue !== null && !isEditing"
      class="absolute left-0.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-zinc-600 text-white text-[9px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 z-10"
      @click.stop="handleReset"
      title="Réinitialiser (revenir à la formule)"
      aria-label="Réinitialiser la valeur calculée par la formule"
    >×</button>

    <!-- Edit mode -->
    <input
      v-if="isEditing"
      ref="inputRef"
      v-model.number="editValue"
      type="number"
      step="1"
      class="absolute inset-0 w-full h-full px-2 py-1.5 text-right text-sm font-bold bg-background/80 border border-primary rounded outline-none tabular-nums z-20 disabled:opacity-50"
      :class="{
        'text-emerald-500 dark:text-emerald-400': type === 'income',
        'text-red-500 dark:text-red-400': type === 'expense',
        'text-indigo-500 dark:text-indigo-400': type === 'parameter',
      }"
      @keydown.enter="confirmEdit"
      @keydown.escape="cancelEdit"
      @blur="confirmEdit"
      :disabled="isReadOnlyMode"
      :aria-label="`Saisie ${monthLabel}`"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import { useBudget } from '../../../composables/useBudget'
import { useToast } from '../../../composables/useToast.js'

// ── Props ─────────────────────────────────────────────────────────────────────

const props = defineProps<{
  categoryName: string
  monthLabel:   string
  type:         'income' | 'expense' | 'parameter'
  storedValue:  number | null
  resolvedValue: number | null   // computed from formula (or same as storedValue if no formula)
  hasFormula:   boolean
  isManualOverride: boolean
  isCurrentMonth: boolean
  isNonEditable?: boolean
}>()

// ── Emits ─────────────────────────────────────────────────────────────────────

const emit = defineEmits<{
  save:  [amount: number]
  reset: []
}>()

// ── State & Composables ───────────────────────────────────────────────────────

const toast = useToast()
const { isReadOnlyMode } = useBudget()

const isEditing = ref(false)
const editValue = ref<number | string>('')
const inputRef  = ref<HTMLInputElement | null>(null)

// ── Computed ──────────────────────────────────────────────────────────────────

/** The value to display: resolved (formula or stored) */
const displayedAmount = computed(() => props.resolvedValue ?? props.storedValue)

const displayValue = computed(() => {
  if (displayedAmount.value === null || displayedAmount.value === undefined) return '—'
  if (props.type === 'parameter') {
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(displayedAmount.value)
  }
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(displayedAmount.value)
})

const cellClass = computed(() => ({
  'bg-primary/5': props.isCurrentMonth,
}))

const valueClass = computed(() => {
  if (props.isNonEditable) {
    if (displayedAmount.value === null) return 'text-muted-foreground/30 cursor-not-allowed'
    if (props.type === 'parameter') return 'text-indigo-500/80 dark:text-indigo-400/80 cursor-not-allowed'
    if (props.type === 'income')  return 'text-emerald-500/80 dark:text-emerald-400/80 cursor-not-allowed'
    return 'text-red-500/80 dark:text-red-400/80 cursor-not-allowed'
  }
  if (displayedAmount.value === null) return 'text-muted-foreground/30 hover:text-muted-foreground/60 hover:bg-muted/30'
  if (props.type === 'parameter') return 'text-indigo-500 dark:text-indigo-400 hover:bg-indigo-500/10'
  if (props.type === 'income')  return 'text-emerald-500 dark:text-emerald-400 hover:bg-emerald-500/10'
  return 'text-red-500 dark:text-red-400 hover:bg-red-500/10'
})

const editTitle = computed(() => {
  if (props.isNonEditable || isReadOnlyMode.value) return 'Cette catégorie n\'est pas modifiable'
  if (props.hasFormula && !props.isManualOverride) return 'Cliquer pour forcer une valeur manuelle (ignorera la formule pour ce mois)'
  return 'Cliquer pour modifier'
})

// ── Actions ───────────────────────────────────────────────────────────────────

function startEdit(): void {
  if (props.isNonEditable || isReadOnlyMode.value) {
    if (props.isNonEditable) toast.info('Cette catégorie est modifiée automatiquement par formule')
    return
  }
  editValue.value = displayedAmount.value ?? ''
  isEditing.value = true
  nextTick(() => {
    inputRef.value?.select()
  })
}

function confirmEdit(): void {
  if (!isEditing.value) return
  isEditing.value = false
  const stringValue = String(editValue.value ?? '')
  const trimmed = stringValue.trim()
  if (trimmed === '') {
    if (props.storedValue !== null) {
      emit('reset')
    }
    return
  }

  const parsed = parseFloat(trimmed)
  if (!isNaN(parsed) && parsed !== (props.storedValue ?? undefined)) {
    emit('save', parsed)
  }
}

function cancelEdit(): void {
  isEditing.value = false
}
</script>
